import { CONTACT, MAP, STUDIO } from "@/config";
import { MapPin, Navigation, Star } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const feedback = [
  {
    quote:
      "Six curated walls and four movable backdrops in one space — change your entire look between shots without changing locations.",
    author: "Every Setting, One Booking",
    context: "Photography & Content Shoots",
  },
  {
    quote:
      "Fully air-conditioned with a separate makeup & changing room, pantry and high-speed Wi-Fi — your crew stays comfortable start to finish.",
    author: "Comfort Built In",
    context: "Long Shoot Days",
  },
];

export const Vouchers = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data.reviews || []))
      .catch(() => setReviews([]));
  }, []);

  const hasReviews = reviews.length > 0;

  return (
  <>
    <section data-testid="feedback-section" className="py-16 bg-white border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="heavy-heading text-2xl font-extrabold text-ink">
            {hasReviews ? "What Our Customers Say" : "Why Creators Pick Room 9"}
          </h2>
          <p className="text-xs text-muted-g mt-1">
            {hasReviews ? "Genuine reviews from people who booked the studio." : "The details that make shoots here easier."}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {hasReviews
            ? reviews.map((r, i) => (
                <figure
                  key={r.id}
                  data-testid={`review-card-${i}`}
                  className="p-6 bg-bg rounded-xl border border-line space-y-3"
                >
                  <div className="flex gap-1" aria-label={`${r.rating} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={16}
                        className={s <= r.rating ? "fill-amber-400 text-amber-400" : "text-line-2"}
                      />
                    ))}
                  </div>
                  <blockquote className="text-sm text-muted-gd leading-relaxed">"{r.text}"</blockquote>
                  <figcaption className="pt-3 border-t border-line text-xs font-bold text-ink">
                    <span data-testid={`review-name-${i}`}>{r.name}</span>
                  </figcaption>
                </figure>
              ))
            : feedback.map((f, i) => (
                <figure
                  key={f.author}
                  data-testid={`feedback-card-${i}`}
                  className="p-6 bg-bg rounded-xl border border-line space-y-3"
                >
                  <blockquote className="text-sm text-muted-gd leading-relaxed">{f.quote}</blockquote>
                  <figcaption className="pt-3 border-t border-line text-xs font-bold text-ink flex justify-between">
                    <span>{f.author}</span>
                    <span className="text-muted-g font-normal">{f.context}</span>
                  </figcaption>
                </figure>
              ))}
        </div>
      </div>
    </section>

    <section id="studio-map" data-testid="location-section" className="py-16 bg-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5 space-y-3">
            <h2 className="heavy-heading text-2xl sm:text-3xl font-extrabold text-ink">Visit Room 9 — SG Highway, Ahmedabad</h2>
            <div className="text-xs text-muted-gd pt-3 border-t border-line grid grid-cols-[110px_1fr] gap-y-2">
              <strong className="text-ink">Address:</strong>
              <span data-testid="location-address">{CONTACT.address}</span>
              <strong className="text-ink">Operating Hours:</strong>
              <span>{CONTACT.hours}</span>
              <strong className="text-ink">Parking:</strong>
              <span>On-site creator and crew parking</span>
            </div>
            <a
              href={MAP.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-directions-button"
              className="btn-primary !px-5 !py-2.5 mt-4"
            >
              <Navigation size={14} /> Get Directions
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="relative rounded-xl overflow-hidden border border-line-2 shadow-sm">
              <iframe
                title="Studio location map — Akshat Tower, SG Highway, Bodakdev"
                data-testid="location-map"
                src={MAP.embedUrl}
                className="w-full h-[320px] sm:h-[420px] lg:h-[480px] border-0 block"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 pointer-events-none">
                <span className="inline-flex items-center gap-2 bg-forest text-white px-3 py-2 rounded-lg text-xs font-bold shadow-lg">
                  <MapPin size={14} />
                  {STUDIO.fullName} — Akshat Tower
                </span>
                <span className="hidden sm:inline text-[11px] text-ink bg-white/90 px-2 py-1 rounded">
                  Near Pakwan Circle, SG Highway
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
};
